import { ErrorModal, Toast } from '@modusign/design-system';
import { useMutation } from 'react-query';

import AttachmentListModal from '@app/features/apply-react-signing/components/AttachmentListModal';
import { AttachmentInformation } from '@app/features/apply-react-signing/libs/getAttachedAttachments';
import { useDocumentAttachmentFile } from '@app/features/apply-react-signing/libs/hooks/useDocumentAttachmentFile';
import getAttachmentBundleName from '@app/features/documents/utils/getAttachmentBundleName';
import useMemberPermissions from '@app/hooks/useMemberPermission';
import delay from '@app/libs/delay';
import workspaceService from '@app/libs/workspaceService';
import { getDocumentsById } from '@app/store/transport-layer/document/documents';
import { downloadAttachments as downloadAttachmentsApi } from '@app/store/transport-layer/document/documents/attachments/download';
import { downloadFileFromURL } from '@app/store/transport-layer/files';

export interface DocumentAttachmentListModalProps {
  documentId: string;
  isDocumentCompleted: boolean;
  onClose: () => void;
}

interface DocumentAttachmentListModalBaseProps extends DocumentAttachmentListModalProps {
  attachments: AttachmentInformation[];
}

export function DocumentAttachmentListModalBase({
  attachments,
  documentId,
  isDocumentCompleted,
  onClose,
}: DocumentAttachmentListModalBaseProps) {
  const { openDocumentAttachmentFile, downloadDocumentAttachmentFile } =
    useDocumentAttachmentFile();
  const { isPermitted } = useMemberPermissions('EXPORT_DOCUMENT');

  const {
    mutate: downloadAttachments,
    isLoading: isDownloading,
    error,
    reset,
  } = useMutation<void, Error, 'REQUESTER' | 'SIGNER' | 'ALL'>({
    mutationFn: async (type) => {
      const workspaceId = workspaceService.getWorkspaceId();
      const [document, response] = await Promise.all([
        getDocumentsById({
          workspaceId,
          documentId,
        }),
        downloadAttachmentsApi({
          documentId,
          type,
          workspaceId,
        }),
      ]);

      let { url } = response;
      for (let retry = 3; !url && retry >= 0; retry -= 1) {
        // eslint-disable-next-line no-await-in-loop
        await delay(500);
        // eslint-disable-next-line no-await-in-loop
        url = (await downloadAttachmentsApi({ documentId, type, workspaceId })).url;
      }

      if (url) {
        await downloadFileFromURL({
          url,
          saveFileName: getAttachmentBundleName(document.title, type),
        });
      } else {
        Toast.warning({ description: '파일을 압축하고 있습니다. 잠시 후 다시 시도해주세요.' });
      }
    },
  });

  return (
    <>
      <AttachmentListModal
        attachments={attachments}
        enableDownload={isPermitted}
        isDocumentCompleted={isDocumentCompleted}
        isDownloading={isDownloading}
        isOpen
        onClose={onClose}
        onDownloadAttachmentBundleClick={downloadAttachments}
        onDownloadAttachmentClick={downloadDocumentAttachmentFile}
        onOpenAttachmentClick={openDocumentAttachmentFile}
      />
      <ErrorModal error={error} isOpen title="다운로드 실패" onClose={() => reset()} />
    </>
  );
}
